import React, { useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
} from "@mui/material";
import {
  GoogleMap,
  Marker,
  useJsApiLoader,
  Autocomplete,
} from "@react-google-maps/api";
import { MarkerF } from "@react-google-maps/api";

const libraries: "places"[] = ["places"];

interface LocationValue {
  lat: number;
  lng: number;
  address: string;
}

interface LocationPickerDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (location: LocationValue) => void;
  initialLocation?: {
    lat: number;
    lng: number;
  };
  mode: "add" | "view";
}

const defaultCenter = { lat: 24.708630647417472, lng: 46.687259674072266 };

export default function LocationPickerDialog({
  open,
  onClose,
  onConfirm,
  initialLocation,
  mode,
}: LocationPickerDialogProps) {
  const { isLoaded } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY ?? "",
    libraries,
  });

  const mapRef = useRef<google.maps.Map | null>(null);

  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);

  const [selectedLocation, setSelectedLocation] = useState(
    initialLocation || defaultCenter,
  );

  const [address, setAddress] = useState("");

  useEffect(() => {
    if (initialLocation) {
      setSelectedLocation(initialLocation);
    }
  }, [initialLocation]);

  const handlePlaceChanged = () => {
    const place = autocompleteRef.current?.getPlace();

    if (!place?.geometry?.location) return;

    const lat = place.geometry.location.lat();
    const lng = place.geometry.location.lng();

    setSelectedLocation({ lat, lng });

    setAddress(place.formatted_address || "");

    mapRef.current?.panTo({ lat, lng });
    mapRef.current?.setZoom(16);
  };

  const handleMapClick = async (event: google.maps.MapMouseEvent) => {
    if (!event.latLng) return;

    const lat = event.latLng.lat();
    const lng = event.latLng.lng();

    setSelectedLocation({ lat, lng });

    const geocoder = new google.maps.Geocoder();

    geocoder.geocode(
      {
        location: { lat, lng },
      },
      (results, status) => {
        if (status === "OK" && results && results.length > 0) {
          setAddress(results[0].formatted_address);

          console.log("Address:", results[0].formatted_address);
        } else {
          console.error("Geocoder failed:", status);
          setAddress("");
        }
      },
    );
  };

  const handleConfirm = () => {
    onConfirm({
      lat: selectedLocation.lat,
      lng: selectedLocation.lng,
      address,
    });
  };

  if (!isLoaded) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Pick Location</DialogTitle>

      <DialogContent>
        <Box sx={{ mt: 2 }}>
          {mode === "add" ? (
            <Autocomplete
              onLoad={(autocomplete) => {
                console.log({ autocomplete });
                autocompleteRef.current = autocomplete;
              }}
              onPlaceChanged={handlePlaceChanged}
            >
              <input
                type="text"
                placeholder="Search for a location..."
                style={{
                  width: "100%",
                  height: 45,
                  padding: "0 12px",
                  border: "1px solid #ccc",
                  borderRadius: 8,
                  marginBottom: 16,
                  fontSize: 14,
                  boxSizing: "border-box",
                }}
              />
            </Autocomplete>
          ) : null}

          <GoogleMap
            mapContainerStyle={{
              width: "100%",
              height: "450px",
            }}
            center={selectedLocation}
            zoom={13}
            onLoad={(map) => {
              mapRef.current = map;
            }}
            onClick={handleMapClick}
            options={{
              streetViewControl: false,
              mapTypeControl: false,
            }}
          >
            <MarkerF
              position={selectedLocation}
              onLoad={() => console.log("Marker loaded")}
            />
          </GoogleMap>
        </Box>
      </DialogContent>

      {mode === "add" ? (
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>

          <Button variant="contained" onClick={handleConfirm}>
            Confirm
          </Button>
        </DialogActions>
      ) : null}
    </Dialog>
  );
}
